import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import Swal from "sweetalert2";
import Footer from "../components/footer";
import Header from "../components/header";
import SeoLinksSection from "../components/SeoLinksSection";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "./utils/utils";

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe("pk_test_51SbGkNPEGGZ0nEjmCUo6kF1yXmVGgmJKbkXuN9v7dOmk81yiMpQyCfLPJlef8VGtymk9XBt28TCjoKHJ2xAlfxDf00ZnojXjKw");

// ⚠️ SECURITY WARNING: Never expose secret keys in production!
// Secret keys should only be used server-side. This is for testing only.
const STRIPE_SECRET_KEY = "sk_test_51SbGkNPEGGZ0nEjm9dqJBl5EUvd6NlGBGx3nSXnavODQOjxqBGVHBweMMcCLkut2wAYjmDquZ1tQDPSrllEgOmz900rD13srGS";

// Stripe API base URL
const STRIPE_API_URL = "https://api.stripe.com/v1";

export default function BookingConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    vehicle_type_id: "",
    vehicleTypeName: "",
    vehicleTypePrice: "",
    vehicleTypeLocation: "",
    tripType: "",
    pickUp: "",
    dropOff: "",
    pickUpLat: "",
    pickUpLng: "",
    dropOffLat: "",
    dropOffLng: "",
    flightArrival: "",
    returnDate: "",
    adults: 0,
    children: 0,
    infants: 0,
    distanceMiles: "",
    totalPrice: "",
  });

  const [userInfo, setUserInfo] = useState({
    salutation: "",
    firstName: "",
    surName: "",
    email: "",
    phone: "",
    pickupTerminal: "",
    dropoffTerminal: "",
    flightNumber: "",
    returnflightNumber: "",
    notes: "",
    company_id: 1,
  });

  const [luggage, setLuggage] = useState({
    small: 1,
    medium: 1,
    large: 1,
  });

  const [editingDate, setEditingDate] = useState(null); // 'flightArrival' or 'returnDate'
  const [paymentMethod, setPaymentMethod] = useState("");

  // Auto-select Google Pay on Chrome, Apple Pay on Safari
  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && /Google Inc/.test(navigator.vendor || "");
    if (isSafari) setPaymentMethod((prev) => prev || "apple_pay");
    else if (isChrome) setPaymentMethod((prev) => prev || "google_pay");
    else setPaymentMethod((prev) => prev || "visa_card");
  }, []);

  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: "",
    cardName: "",
    expiryDate: "",
    cvv: "",
    billingAddress: "",
    postcode: "",
  });
  const dateInputRef = useRef(null);

  const calculateTotal = useCallback(() => {
    // bookingDetails.totalPrice already reflects the correct overall total
    return parseFloat(bookingDetails.totalPrice) || 0;
  }, [bookingDetails.totalPrice]);

  const isRoundTrip =
    bookingDetails.tripType === "Round" || bookingDetails.tripType === "roundtrip";

  const totalPriceNumber = parseFloat(bookingDetails.totalPrice) || 0;
  const perTripPrice = isRoundTrip && totalPriceNumber ? totalPriceNumber / 2 : totalPriceNumber;
  const isPickupAirport = /airport/i.test(bookingDetails.pickUp || "");
  const estimatedMinutes =
    bookingDetails.distanceMiles && !Number.isNaN(Number(bookingDetails.distanceMiles))
      ? Math.round(Number(bookingDetails.distanceMiles) * 2)
      : null;
  const totalPassengers =
    (bookingDetails.adults || 0) +
    (bookingDetails.children || 0) +
    (bookingDetails.infants || 0);

  // Create Payment Intent directly using Stripe API with secret key
  const createPaymentIntent = async (amount, paymentMethodId, currency = 'gbp') => {
    const formData = new URLSearchParams();
    formData.append('amount', Math.round(amount * 100)); // Convert to cents
    formData.append('currency', currency);
    formData.append('payment_method', paymentMethodId);
    formData.append('confirm', 'true'); // Automatically confirm the payment
    formData.append('return_url', window.location.href);

    const response = await fetch(`${STRIPE_API_URL}/payment_intents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${STRIPE_SECRET_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create payment intent');
    }

    return await response.json();
  };

  // Submit booking and process payment on frontend using secret key
  const submitBookingWithPayment = useCallback(async (paymentMethodId) => {
    setLoading(true);
    setProcessingPayment(true); // Show full-page loader
    
    try {
      const totalAmount = calculateTotal();

      // Step 1: Create and confirm Payment Intent directly using Stripe API
      console.log('Creating Payment Intent with amount:', totalAmount);
      const paymentIntent = await createPaymentIntent(totalAmount, paymentMethodId);

      if (!paymentIntent) {
        throw new Error('Failed to create payment intent');
      }

      console.log('Payment Intent created:', paymentIntent);

      // Check payment status
      if (paymentIntent.status === 'succeeded') {
        // Payment successful - now submit booking
        // Get amount paid from payment intent (it's in cents, convert to pounds)
        const amountPaid = (paymentIntent.amount / 100).toFixed(2);
        
        const bookingData = {
          ...bookingDetails,
          ...userInfo,
          luggageSmall: luggage.small,
          luggageMedium: luggage.medium,
          luggageLarge: luggage.large,
          carSeat: bookingDetails.carSeat ?? 0,
          totalAmount: totalAmount,
          paymentMethod: paymentMethod || 'visa_card',
          // Terminal name only when pickup from airport (one-way or round trip)
          terminal_name: isPickupAirport ? (userInfo.pickupTerminal || '') : '',
          flight_number: isRoundTrip && isPickupAirport ? userInfo.flightNumber : '',
          stripePaymentMethodId: paymentMethodId,
          stripePaymentIntentId: paymentIntent.id,
          paymentReference: paymentIntent.id, // Payment reference (Payment Intent ID)
          amountPaid: amountPaid, // Amount actually paid
          paymentStatus: 'succeeded',
        };

        const bookingResponse = await fetch(`${API_URL}bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData),
        });

        if (!bookingResponse.ok) {
          throw new Error('Booking failed');
        }

        const result = await bookingResponse.json();
        console.log('Booking confirmed:', result);
        setProcessingPayment(false);
        await Swal.fire({
          icon: 'success',
          title: 'Payment Successful!',
          text: `Booking Confirmed. Your Booking Number is: ${result.booking_no || 'N/A'}`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745',
        });
        navigate('/');
      } else if (paymentIntent.status === 'requires_action') {
        // 3D Secure or other authentication required
        const stripe = await stripePromise;
        if (stripe && paymentIntent.client_secret) {
          const { paymentIntent: confirmedIntent, error: confirmError } = await stripe.confirmCardPayment(
            paymentIntent.client_secret,
            {
              payment_method: paymentMethodId,
            }
          );

          if (confirmError) {
            setProcessingPayment(false);
            await Swal.fire({
              icon: 'error',
              title: 'Payment Authentication Failed',
              text: confirmError.message,
              confirmButtonText: 'OK',
              confirmButtonColor: '#dc3545',
            });
            setLoading(false);
            return;
          }

          if (confirmedIntent && confirmedIntent.status === 'succeeded') {
            // Payment successful after authentication - submit booking
            // Get amount paid from payment intent (it's in cents, convert to pounds)
            const amountPaid = (confirmedIntent.amount / 100).toFixed(2);
            
            const bookingData = {
              ...bookingDetails,
              ...userInfo,
              luggageSmall: luggage.small,
              luggageMedium: luggage.medium,
              luggageLarge: luggage.large,
              carSeat: bookingDetails.carSeat ?? 0,
              totalAmount: totalAmount,
              paymentMethod: paymentMethod || 'visa_card',
              // Terminal name only when pickup from airport (one-way or round trip)
              terminal_name: isPickupAirport ? (userInfo.pickupTerminal || '') : '',
              flight_number: isRoundTrip && isPickupAirport ? userInfo.flightNumber : '',
              stripePaymentMethodId: paymentMethodId,
              stripePaymentIntentId: confirmedIntent.id,
              paymentReference: confirmedIntent.id, // Payment reference (Payment Intent ID)
              amountPaid: amountPaid, // Amount actually paid
              paymentStatus: 'succeeded',
            };

            const bookingResponse = await fetch(`${API_URL}bookings`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(bookingData),
            });

            if (!bookingResponse.ok) {
              throw new Error('Booking failed');
            }

            const result = await bookingResponse.json();
            console.log('Booking confirmed:', result);
            setProcessingPayment(false);
            await Swal.fire({
              icon: 'success',
              title: 'Payment Successful!',
              text: `Booking confirmed. Booking ID: ${confirmedIntent.id || 'N/A'}`,
              confirmButtonText: 'OK',
              confirmButtonColor: '#28a745',
            });
            navigate('/');
          } else {
            setProcessingPayment(false);
            await Swal.fire({
              icon: 'warning',
              title: 'Payment Status',
              text: `Payment status: ${confirmedIntent?.status}. Please try again.`,
              confirmButtonText: 'OK',
              confirmButtonColor: '#ffc107',
            });
            setLoading(false);
          }
        } else {
          setProcessingPayment(false);
          await Swal.fire({
            icon: 'warning',
            title: 'Authentication Required',
            text: 'Payment requires additional authentication. Please try again.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#ffc107',
          });
          setLoading(false);
        }
      } else {
        setProcessingPayment(false);
        await Swal.fire({
          icon: 'warning',
          title: 'Payment Status',
          text: `Payment status: ${paymentIntent.status}. Please try again.`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#ffc107',
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setProcessingPayment(false);
      await Swal.fire({
        icon: 'error',
        title: 'Payment Error',
        text: error.message || 'An error occurred while processing your payment. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
      setLoading(false);
    }
  }, [bookingDetails, userInfo, luggage, paymentMethod, calculateTotal, navigate]);

  useEffect(() => {
    // Load booking details from URL parameters
    setBookingDetails({
      vehicle_type_id: searchParams.get("serviceId") || "",
      vehicleTypeName: searchParams.get("serviceTitle") || "",
      vehicleTypePrice: searchParams.get("totalPrice") || "",
      vehicleTypeLocation: searchParams.get("vehicleTypeLocation") || "",
      tripType: searchParams.get("tripType") || "",
      pickUp: searchParams.get("pickUp") || "",
      dropOff: searchParams.get("dropOff") || "",
      pickUpLat: searchParams.get("pickUpLat") || "",
      pickUpLng: searchParams.get("pickUpLng") || "",
      dropOffLat: searchParams.get("dropOffLat") || "",
      dropOffLng: searchParams.get("dropOffLng") || "",
      flightArrival: searchParams.get("flightArrival") || "",
      returnDate: searchParams.get("returnDate") || "",
      adults: parseInt(searchParams.get("adults") || "1"),
      children: parseInt(searchParams.get("children") || "0"),
      infants: parseInt(searchParams.get("infants") || "0"),
      carSeat: parseInt(searchParams.get("carSeat") || "0"),
      distanceMiles: searchParams.get("distanceMiles") || "",
      totalPrice: searchParams.get("totalPrice") || "",
    });

    // setLuggage({
    //   small: parseInt(searchParams.get("vehicleSmallLuggage") || "0"),
    //   medium: parseInt(searchParams.get("vehicleMediumLuggage") || "0"),
    //   large: parseInt(searchParams.get("vehicleLargeLuggage") || "0"),
    // });
  }, [searchParams]);

  // Pre-fill user information if logged in
  useEffect(() => {
    if (isAuthenticated() && user) {
      setUserInfo((prev) => ({
        ...prev,
        salutation: user.title || prev.salutation || "",
        firstName: user.first_name || prev.firstName || "",
        surName: user.last_name || prev.surName || "",
        email: user.email || prev.email || "",
        phone: user.contact_no || prev.phone || "",
      }));
    }
  }, [user, isAuthenticated]);

  const fetchProfileDetailsByEmail = async (email) => {
    if (!email) return;

    try {
      const response = await fetch(
        `${API_URL}getProfileDetails/${encodeURIComponent(email)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch profile details");
      }

      const result = await response.json();

      if (result.status === "success" && Array.isArray(result.data) && result.data.length > 0) {
        const profile = result.data[0];

        setUserInfo((prev) => ({
          ...prev,
          salutation: profile.title ?? prev.salutation,
          firstName: profile.first_name ?? prev.firstName,
          surName: profile.last_name ?? prev.surName,
          email: profile.email ?? prev.email,
          phone: profile.contact_no ?? prev.phone,
        }));
      } else {
        await Swal.fire({
          icon: "info",
          title: "No Profile Found",
          text: "No passenger profile was found for this email address.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching profile details:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch profile details. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const fetchProfileDetailsByPhone = async (phone) => {
    if (!phone) return;

    try {
      const response = await fetch(
        `${API_URL}getProfileDetails/${encodeURIComponent(phone)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch profile details");
      }

      const result = await response.json();

      if (result.status === "success" && Array.isArray(result.data) && result.data.length > 0) {
        const profile = result.data[0];

        setUserInfo((prev) => ({
          ...prev,
          salutation: profile.title ?? prev.salutation,
          firstName: profile.first_name ?? prev.firstName,
          surName: profile.last_name ?? prev.surName,
          email: profile.email ?? prev.email,
          phone: profile.contact_no ?? prev.phone,
        }));
      } else {
        await Swal.fire({
          icon: "info",
          title: "No Profile Found",
          text: "No passenger profile was found for this phone number.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching profile details by phone:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch profile details. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (field, value) => {
    setBookingDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateSave = (field) => {
    // Use setTimeout to avoid React DOM conflicts
    setTimeout(() => {
      setEditingDate(null);
    }, 0);
  };

  const handleDateCancel = () => {
    // Use setTimeout to avoid React DOM conflicts
    setTimeout(() => {
      setEditingDate(null);
    }, 0);
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingDate && dateInputRef.current) {
      setTimeout(() => {
        dateInputRef.current?.focus();
      }, 0);
    }
  }, [editingDate]);

  const handlePaymentInfoChange = (e) => {
    const { name, value } = e.target;
    setPaymentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Payment form component that uses Stripe Elements
  const PaymentForm = ({ onSubmit, loading: formLoading }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Validate required fields
      if (!userInfo.firstName || !userInfo.surName || !userInfo.email || !userInfo.phone) {
        await Swal.fire({
          icon: 'warning',
          title: 'Required Fields Missing',
          text: 'Please fill in all required fields.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ffc107',
        });
        return;
      }

      // Validate terminal fields when airport is involved
      if (isPickupAirport && !userInfo.pickupTerminal) {
        await Swal.fire({
          icon: 'warning',
          title: 'Pickup Terminal Required',
          text: 'Please enter the pickup terminal for the airport.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ffc107',
        });
        return;
      }

      // Validate flight number when round trip with airport pickup
      if (isRoundTrip && isPickupAirport && !userInfo.flightNumber) {
        await Swal.fire({
          icon: 'warning',
          title: 'Flight Number Required',
          text: 'Please enter the flight number for your round trip airport pickup.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ffc107',
        });
        return;
      }
      
      if (!stripe || !elements) {
        await Swal.fire({
          icon: 'error',
          title: 'Stripe Not Loaded',
          text: 'Stripe is not loaded. Please refresh the page.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
        });
        return;
      }

      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        await Swal.fire({
          icon: 'error',
          title: 'Card Element Not Found',
          text: 'Card element not found. Please refresh the page.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
        });
        return;
      }

      // Set loading state
      if (onSubmit) {
        // Create payment method using Stripe API directly
        try {
          const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
              name: `${userInfo.firstName} ${userInfo.surName}`,
              email: userInfo.email,
              phone: userInfo.phone,
            },
          });

          if (pmError) {
            await Swal.fire({
              icon: 'error',
              title: 'Payment Error',
              text: pmError.message,
              confirmButtonText: 'OK',
              confirmButtonColor: '#dc3545',
            });
            return;
          }

          if (!paymentMethod || !paymentMethod.id) {
            await Swal.fire({
              icon: 'error',
              title: 'Payment Method Creation Failed',
              text: 'Failed to create payment method. Please try again.',
              confirmButtonText: 'OK',
              confirmButtonColor: '#dc3545',
            });
            return;
          }

          // Call the parent submit handler with payment method ID
          onSubmit(paymentMethod.id);
        } catch (error) {
          console.error('Error creating payment method:', error);
          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An error occurred while processing your payment. Please try again.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#dc3545',
          });
        }
      }
    };

    const cardElementOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    };

    return (
      <div>
        <div className="mb-3">
          <label className="form-label fw-bold mb-2" style={{ fontSize: "0.9rem" }}>
            Card Details <span className="text-danger">*</span>
          </label>
          <div className="border rounded p-3" style={{ backgroundColor: '#fff' }}>
            <CardElement options={cardElementOptions} />
          </div>
        </div>
        <button
          type="button"
          className="nir-btn w-100"
          style={{ padding: "8px 16px", fontSize: "0.95rem" }}
          disabled={!stripe || formLoading}
          onClick={handleSubmit}
        >
          {formLoading ? "Processing Payment..." : "Confirm Booking & Pay"}
        </button>
      </div>
    );
  };

  // Handle non-card payment submissions
  const submitBooking = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!userInfo.firstName || !userInfo.surName || !userInfo.email || !userInfo.phone) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields Missing',
        text: 'Please fill in all required fields.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ffc107',
      });
      return;
    }

    // Validate terminal fields when airport is involved
    if (isPickupAirport && !userInfo.pickupTerminal) {
      Swal.fire({
        icon: 'warning',
        title: 'Pickup Terminal Required',
        text: 'Please enter the pickup terminal for the airport.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ffc107',
      });
      return;
    }

    // Validate flight number when round trip with airport pickup
    if (isRoundTrip && isPickupAirport && !userInfo.flightNumber) {
      Swal.fire({
        icon: 'warning',
        title: 'Flight Number Required',
        text: 'Please enter the flight number for your round trip airport pickup.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ffc107',
      });
      return;
    }

    if (!paymentMethod) {
      Swal.fire({
        icon: 'warning',
        title: 'Payment Method Required',
        text: 'Please select a payment method.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ffc107',
      });
      return;
    }

    // Card payments are handled by PaymentForm component - prevent form submission
    if (paymentMethod === "visa_card" || paymentMethod === "master_card") {
      e.preventDefault();
      e.stopPropagation();
      return; // PaymentForm will handle this
    }

    setLoading(true);

    const bookingData = {
      ...bookingDetails,
      ...userInfo,
      luggageSmall: luggage.small,
      luggageMedium: luggage.medium,
      luggageLarge: luggage.large,
      carSeat: bookingDetails.carSeat ?? 0,
      totalAmount: calculateTotal(),
      paymentMethod: paymentMethod,
      // Terminal name only when pickup from airport (one-way or round trip)
      terminal_name: isPickupAirport ? (userInfo.pickupTerminal || '') : '',
      flight_number: isRoundTrip && isPickupAirport ? userInfo.flightNumber : '',
    };

    try {
      // For non-card payments, submit booking directly
      const response = await fetch(`${API_URL}bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Booking failed');
      }

      const result = await response.json();
      console.log(result);
      await Swal.fire({
        icon: 'success',
        title: 'Booking Confirmed!',
        text: `Booking ID: ${result.bookingId || 'N/A'}`,
        confirmButtonText: 'OK',
        confirmButtonColor: '#28a745',
      });
      navigate('/');
    } catch (error) {
      console.error('Booking error:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Booking Error',
        text: 'An error occurred. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full-page Loader */}
      {processingPayment && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: "center", color: "#fff" }}>
            <div
              className="spinner-border"
              style={{
                width: "4rem",
                height: "4rem",
                borderWidth: "0.5rem",
                borderColor: "#fff transparent",
              }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 style={{ marginTop: "20px", color: "#fff" }}>
              Processing Payment...
            </h4>
            <p style={{ marginTop: "10px", color: "#fff" }}>
              Please wait while we process your payment
            </p>
          </div>
        </div>
      )}
      <Header />
      <>
        {/* BreadCrumb Starts */}

        {/* BreadCrumb Ends */}

        {/* Booking Confirmation Section */}
        <section className="trending pt-2 pb-2 bg-lgrey">
          <div className="container">
            <div className="row">
              {/* Left Column - Your Information Form */}
              <div className="col-lg-8">
                <div className="bg-white rounded p-3 mb-3" style={{ border: "1px solid #d70015" }}>
                  <h4 className="mb-3" style={{ fontSize: "1.1rem" }}>Your Information</h4>
                  <form onSubmit={submitBooking}>
                    {/* Row 1: Phone only */}
                    <div className="row g-2 mb-2">
                      <div className="col-12">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                          Phone <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={userInfo.phone}
                          onChange={handleInputChange}
                          onBlur={(e) => fetchProfileDetailsByPhone(e.target.value)}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px", width: "140px", maxWidth: "140px" }}
                          required
                        />
                      </div>
                    </div>
                    {/* Row 2: All other fields */}
                    <div className="row g-2">
                      {isPickupAirport && (
                        <div className="col-md-4">
                          <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                            Pickup Terminal <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="pickupTerminal"
                            value={userInfo.pickupTerminal}
                            onChange={handleInputChange}
                            className="form-control border"
                            style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                            placeholder="e.g. Terminal 3"
                          />
                        </div>
                      )}

                      <div className="col-md-2">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                          Title
                        </label>
                        <select
                          name="salutation"
                          value={userInfo.salutation}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                        >
                          <option value="">Select</option>
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Miss">Miss</option>
                          <option value="Ms">Ms</option>
                          <option value="Prof">Prof</option>
                          <option value="Dr">Dr</option>
                          <option value="Sir">Sir</option>
                          <option value="Lady">Lady</option>
                          <option value="Lord">Lord</option>
                          
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={userInfo.firstName}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="surName"
                          value={userInfo.surName}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                          required
                        />
                      </div>

                      <div className="col-md-4">
                          <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>
                              Email <span className="text-danger">*</span>
                          </label>
                          <input
                              type="email"
                              name="email"
                              value={userInfo.email}
                              onChange={handleInputChange}
                              onBlur={(e) => fetchProfileDetailsByEmail(e.target.value)}
                              className="form-control border"
                              style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                              required
                          />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>Flight Number</label>
                        <input
                          type="text"
                          name="flightNumber"
                          value={userInfo.flightNumber}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                          placeholder="e.g. BA123"
                        />
                      </div>
                      {bookingDetails.tripType === "Round" && (
                      <div className="col-md-4">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>Return Flight Number</label>
                        <input
                          type="text"
                          name="returnflightNumber"
                          value={userInfo.returnflightNumber}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                          placeholder="e.g. BA123"
                        />
                      </div>
                      )}
                      <div className="col-md-12">
                        <label className="form-label fw-bold mb-1" style={{ fontSize: "0.9rem" }}>Notes</label>
                        <input
                          type="text"
                          name="notes"
                          value={userInfo.notes}
                          onChange={handleInputChange}
                          className="form-control border"
                          style={{ padding: "6px 12px", fontSize: "0.9rem", height: "38px" }}
                          placeholder="Any special requests..."
                        />
                      </div>
                    </div>

                    {/* Payment Section */}
                    <div className="mt-3 pt-3 border-top">
                      <h5 className="mb-3" style={{ fontSize: "1rem" }}>Payment</h5>
                      
                      {/* Payment Method Selection - Radio buttons with icons */}
                      <div className="mb-3">
                        <label className="form-label fw-bold mb-2" style={{ fontSize: "0.9rem" }}>
                          Payment Method <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex flex-wrap gap-3">
                          {[
                            { value: "visa_card", label: "Visa Card", icon: "fa-brands fa-cc-visa", iconStyle: { fontSize: "1.5rem", color: "#1a1f71" } },
                            { value: "master_card", label: "Master Card", icon: "fa-brands fa-cc-mastercard", iconStyle: { fontSize: "1.5rem", color: "#eb001b" } },
                            { value: "google_pay", label: "Google Pay", icon: "fa-brands fa-google-pay", iconStyle: { fontSize: "1.5rem" } },
                            { value: "apple_pay", label: "Apple Pay", icon: "fa-brands fa-apple-pay", iconStyle: { fontSize: "1.5rem" } },
                          ].map((opt) => (
                            <label
                              key={opt.value}
                              className="d-flex align-items-center gap-2 border rounded p-2 mb-0 cursor-pointer"
                              style={{
                                cursor: "pointer",
                                borderColor: paymentMethod === opt.value ? "#d70015" : "#dee2e6",
                                borderWidth: paymentMethod === opt.value ? 2 : 1,
                                minWidth: "140px",
                              }}
                            >
                              <input
                                type="radio"
                                name="paymentMethod"
                                value={opt.value}
                                checked={paymentMethod === opt.value}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="form-check-input"
                              />
                            <span className={opt.icon} style={opt.iconStyle} />
                              <span style={{ fontSize: "0.9rem" }}>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Stripe Elements Payment Form for Card Payments - en-GB so postal code accepts UK alphanumeric postcodes */}
                      {(paymentMethod === "visa_card" || paymentMethod === "master_card") && (
                        <Elements stripe={stripePromise} options={{ locale: "en-GB" }}>
                          <PaymentForm onSubmit={submitBookingWithPayment} loading={loading} />
                        </Elements>
                      )}

                      {/* Google Pay Info */}
                      {paymentMethod === "google_pay" && (
                        <div className="alert alert-info mb-0" style={{ fontSize: "0.85rem" }}>
                          <span className="fa-brands fa-google-pay me-2"></span>
                          You will complete payment with Google Pay at checkout.
                        </div>
                      )}

                      {/* Apple Pay Info */}
                      {paymentMethod === "apple_pay" && (
                        <div className="alert alert-info mb-0" style={{ fontSize: "0.85rem" }}>
                          <span className="fa-brands fa-apple-pay me-2"></span>
                          You will complete payment with Apple Pay at checkout.
                        </div>
                      )}
                    </div>

                    {/* Submit button - only show for wallet payments (Google/Apple Pay when no card form) */}
                    {paymentMethod && paymentMethod !== "visa_card" && paymentMethod !== "master_card" && (
                      <div className="mt-3">
                        <button
                          type="submit"
                          className="nir-btn w-100"
                          style={{ padding: "8px 16px", fontSize: "0.95rem" }}
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Confirm Booking"}
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* Right Column - Journey Details + Price Summary */}
              <div className="col-lg-4">
                {/* Booking Summary */}
                <div className="bg-white rounded p-3 mb-3" style={{ border: "1px solid #d70015" }}>
                  <h5 className="mb-2" style={{ fontSize: "1rem" }}>
                    <span className="icon-directions theme me-2" />
                    Booking Summary
                  </h5>
                  {/* Journey details */}
                  {isRoundTrip ? (
                    <>
                      {/* Trip 1 */}
                      <div className="mb-2" style={{ fontSize: "0.85rem" }}>
                        <div className="fw-bold mb-1" style={{ color: "#d70015" }}>
                          Trip 1
                        </div>
                        {/* Trip 1 date/time */}
                        <div className="mb-1 d-flex align-items-start">
                          <span className="fa fa-calendar-alt me-2 mt-1 theme" style={{ fontSize: "0.9rem" }} />
                          <div style={{ fontSize: "0.85rem" }}>
                            {bookingDetails.flightArrival
                              ? new Date(bookingDetails.flightArrival).toLocaleString()
                              : "-"}
                          </div>
                        </div>
                        {/* Trip 1 pickup */}
                        <div className="mb-1 d-flex align-items-start">
                          <span
                            className="fa fa-map-marker-alt me-2 mt-1"
                            style={{ color: "#28a745", fontSize: "0.9rem" }}
                          />
                          <div style={{ fontSize: "0.85rem" }}>{bookingDetails.pickUp || "-"}</div>
                        </div>
                        {/* Trip 1 drop off */}
                        <div className="mb-1 d-flex align-items-start">
                          <span
                            className="fa fa-map-marker me-2 mt-1 text-danger"
                            style={{ fontSize: "0.9rem" }}
                          />
                          <div style={{ fontSize: "0.85rem" }}>{bookingDetails.dropOff || "-"}</div>
                        </div>
                        {/* Trip 1 charges (single-trip amount) */}
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <span style={{ fontSize: "0.9rem", color: "#d70015", fontWeight: "600" }}>Trip Charges</span>
                          <strong className="theme" style={{ fontSize: "0.95rem" }}>
                            £{perTripPrice.toFixed(2)}
                          </strong>
                        </div>
                      </div>

                      {/* Trip 2 */}
                      <div className="mb-2" style={{ fontSize: "0.85rem" }}>
                        <div className="fw-bold mb-1" style={{ color: "#d70015" }}>
                          Trip 2
                        </div>
                        {/* Trip 2 date/time (return) */}
                        <div className="mb-1 d-flex align-items-start">
                          <span className="fa fa-calendar-alt me-2 mt-1 theme" style={{ fontSize: "0.9rem" }} />
                          <div style={{ fontSize: "0.85rem" }}>
                            {bookingDetails.returnDate
                              ? ` ${new Date(bookingDetails.returnDate).toLocaleString()}`
                              : "-"}
                          </div>
                        </div>
                        {/* Trip 2 pickup (reverse) */}
                        <div className="mb-1 d-flex align-items-start">
                          <span
                            className="fa fa-map-marker-alt me-2 mt-1"
                            style={{ color: "#28a745", fontSize: "0.9rem" }}
                          />
                          <div style={{ fontSize: "0.85rem" }}>{bookingDetails.dropOff || "-"}</div>
                        </div>
                        {/* Trip 2 drop off (reverse) */}
                        <div className="mb-1 d-flex align-items-start">
                          <span
                            className="fa fa-map-marker me-2 mt-1 text-danger"
                            style={{ fontSize: "0.9rem" }}
                          />
                          <div style={{ fontSize: "0.85rem" }}>{bookingDetails.pickUp || "-"}</div>
                        </div>
                        {/* Trip 2 charges (single-trip amount) */}
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <span style={{ fontSize: "0.9rem", color: "#d70015", fontWeight: "600" }}>Trip Charges</span>
                          <strong className="theme" style={{ fontSize: "0.95rem" }}>
                            £{perTripPrice.toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Pickup Date/Time first */}
                      <div className="mb-1 d-flex align-items-start">
                        <span className="fa fa-calendar-alt me-2 mt-1 theme" style={{ fontSize: "0.9rem" }} />
                        {editingDate === "flightArrival" ? (
                          <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                            <input
                              ref={dateInputRef}
                              type="datetime-local"
                              className="form-control border"
                              style={{ padding: "4px 8px", fontSize: "0.85rem", height: "32px", flex: 1 }}
                              value={formatDateTimeForInput(bookingDetails.flightArrival)}
                              onChange={(e) => handleDateChange("flightArrival", e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleDateSave("flightArrival");
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  handleDateCancel();
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDateSave("flightArrival");
                              }}
                            >
                              <span className="fa fa-check" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDateCancel();
                              }}
                            >
                              <span className="fa fa-times" />
                            </button>
                          </div>
                        ) : (
                          <div
                            style={{ fontSize: "0.85rem", cursor: "pointer", flex: 1 }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                            title="Click to edit"
                          >
                            {bookingDetails.flightArrival
                              ? new Date(bookingDetails.flightArrival).toLocaleString()
                              : "-"}
                          </div>
                        )}
                      </div>
                      {/* Pick Up */}
                      <div className="mb-1 d-flex align-items-start">
                        <span className="fa fa-map-marker-alt me-2 mt-1" style={{ color: "#28a745", fontSize: "0.9rem" }} />
                        <div style={{ fontSize: "0.85rem" }}>{bookingDetails.pickUp || "-"}</div>
                      </div>
                      {/* Drop Off */}
                      <div className="mb-1 d-flex align-items-start">
                        <span className="fa fa-map-marker me-2 mt-1 text-danger" style={{ fontSize: "0.9rem" }} />
                        <div style={{ fontSize: "0.85rem" }}>{bookingDetails.dropOff || "-"}</div>
                      </div>
                      {/* Estimated time */}
                      {estimatedMinutes && (
                        <div className="mb-1 d-flex align-items-start">
                          <span className="fa fa-clock me-2 mt-1 theme" style={{ fontSize: "0.9rem" }} />
                          <div style={{ fontSize: "0.85rem" }}>
                            Estimated time: approximately {estimatedMinutes} minutes
                          </div>
                        </div>
                      )}
                    </>
                  )}
                    {/* Passengers and luggage in one row */}
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-2" style={{ fontSize: "0.85rem" }}>
                      {/* Passenger info with icon – same pill style as results */}
                        <span
                          className="d-inline-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: 60,
                            height: 32,
                            backgroundColor: "#f5f5f5",
                          }}
                          title={`Passengers: ${totalPassengers}`}
                        >
                          <span className="fa fa-user-friends theme" />
                          <span style={{ marginLeft: 8 }}>
                            {totalPassengers > 0 ? totalPassengers : "—"}
                          </span>
                        </span>
                        {/* Luggage & Child Seat – medium, large, child seat */}
                      <>
                          {luggage.medium > 0 && (
                              <span
                                  className="d-inline-flex align-items-center rounded px-2"
                                  style={{ height: 32, backgroundColor: "#f5f5f5" }}
                                  title={`Medium bags: ${luggage.medium}`}
                              >
                            <span className="fa fa-suitcase-rolling theme me-1" />
                            <span>{luggage.medium}</span>
                          </span>
                          )}
                          {luggage.large > 0 && (
                              <span
                                  className="d-inline-flex align-items-center rounded px-2"
                                  style={{ height: 32, backgroundColor: "#f5f5f5" }}
                                  title={`Large bags: ${luggage.large}`}
                              >
                            <span className="fa fa-suitcase theme me-1" />
                            <span>{luggage.large}</span>
                          </span>
                          )}
                          {(bookingDetails.carSeat || 0) > 0 && (
                              <span
                                  className="d-inline-flex align-items-center rounded px-2"
                                  style={{ height: 32, backgroundColor: "#f5f5f5" }}
                                  title={`Child seat: ${bookingDetails.carSeat}`}
                              >
                            <span className="fa fa-child theme me-1" />
                            <span>{bookingDetails.carSeat}</span>
                          </span>
                          )}
                          {luggage.medium === 0 && luggage.large === 0 && !(bookingDetails.carSeat > 0) && (
                              <span className="d-inline-flex align-items-center" title="No luggage">
                            <span className="fa fa-suitcase theme me-1 opacity-50" />
                            <span className="text-muted">None</span>
                          </span>
                          )}
                      </>
                    </div>

                    {/* Payment summary */}
                    <hr className="my-2" style={{ borderColor: "#d70015", opacity: 1 }} />
                    {/* Payment section: Trip charges, Booking fee, Cancellation Cover, Total Payable */}
                    <div className="mb-1 d-flex justify-content-between">
                      <span style={{ fontSize: "0.9rem" }}>Trip charges</span>
                      <strong className="theme" style={{ fontSize: "0.95rem" }}>
                        £{calculateTotal().toFixed(2)}
                      </strong>
                    </div>
                    <div className="mb-1 d-flex justify-content-between">
                      <span style={{ fontSize: "0.9rem" }}>Booking fee</span>
                      <strong style={{ fontSize: "0.95rem" }}>£0.00</strong>
                    </div>
                    <div className="mb-1 d-flex justify-content-between">
                      <span style={{ fontSize: "0.9rem" }}>Cancellation Cover</span>
                      <strong style={{ fontSize: "0.95rem" }}>£0.00</strong>
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between">
                      <span style={{ fontSize: "0.95rem", fontWeight: "600" }}>Total Payable</span>
                      <strong className="theme" style={{ fontSize: "1.1rem" }}>
                        £{calculateTotal().toFixed(2)}
                      </strong>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SeoLinksSection />
        <Footer />
      </>
    </>
  );
}
